import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, Plus, Star, IndianRupee, CheckCircle2, Edit2, Trash2,
  Loader2, Users, Clock, BadgeCheck, MapPin, ChevronRight,
  X, Eye, ShoppingCart, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const SERVICE_TYPES = [
  "GST Filing", "Income Tax Returns", "Audit & Assurance",
  "Company Registration", "TDS Returns", "Bookkeeping",
  "Payroll Management", "Advisory", "Compliance"
];

interface ServicePackage {
  id: string;
  ca_profile_id: string;
  ca_user_id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  includes: string[];
  service_type: string;
  is_active: boolean;
  max_subscribers: number | null;
  current_subscribers: number;
  created_at: string;
}

interface PackageWithCA extends ServicePackage {
  ca_profiles?: {
    name: string;
    firm_name: string | null;
    city: string;
    avg_rating: number;
    is_verified: boolean;
    experience_years: number;
    avatar_url: string | null;
  } | null;
}

interface Subscription {
  id: string;
  package_id: string;
  subscriber_user_id: string;
  company_name: string;
  status: string;
  started_at: string;
  expires_at: string | null;
  ca_service_packages?: {
    name: string;
    price_monthly: number;
    service_type: string;
    ca_profiles?: { name: string } | null;
  } | null;
}

// ── Create/Edit Package Dialog ──
function PackageFormDialog({
  open, onOpenChange, onSaved, editPkg
}: {
  open: boolean; onOpenChange: (v: boolean) => void; onSaved: () => void;
  editPkg?: ServicePackage | null;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceMonthly, setPriceMonthly] = useState("");
  const [priceYearly, setPriceYearly] = useState("");
  const [serviceType, setServiceType] = useState("GST Filing");
  const [includeItems, setIncludeItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState("");
  const [maxSubs, setMaxSubs] = useState("");

  useEffect(() => {
    if (editPkg) {
      setName(editPkg.name);
      setDescription(editPkg.description || "");
      setPriceMonthly(String(editPkg.price_monthly));
      setPriceYearly(editPkg.price_yearly ? String(editPkg.price_yearly) : "");
      setServiceType(editPkg.service_type);
      setIncludeItems(editPkg.includes || []);
      setMaxSubs(editPkg.max_subscribers ? String(editPkg.max_subscribers) : "");
    } else {
      setName(""); setDescription(""); setPriceMonthly(""); setPriceYearly("");
      setServiceType("GST Filing"); setIncludeItems([]); setMaxSubs("");
    }
  }, [editPkg, open]);

  const addItem = () => {
    if (newItem.trim() && !includeItems.includes(newItem.trim())) {
      setIncludeItems([...includeItems, newItem.trim()]);
      setNewItem("");
    }
  };

  const handleSave = async () => {
    if (!user || !name.trim() || !priceMonthly) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }
    setLoading(true);

    const { data: profile } = await supabase.from("ca_profiles").select("id").eq("user_id", user.id).single();
    if (!profile) {
      toast({ title: "CA profile not found", description: "Create your CA profile first.", variant: "destructive" });
      setLoading(false);
      return;
    }

    const payload = {
      ca_profile_id: profile.id,
      ca_user_id: user.id,
      name: name.trim(),
      description: description.trim() || null,
      price_monthly: parseFloat(priceMonthly),
      price_yearly: priceYearly ? parseFloat(priceYearly) : null,
      includes: includeItems,
      service_type: serviceType,
      max_subscribers: maxSubs ? parseInt(maxSubs) : null,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (editPkg) {
      ({ error } = await supabase.from("ca_service_packages").update(payload as any).eq("id", editPkg.id));
    } else {
      ({ error } = await supabase.from("ca_service_packages").insert(payload as any));
    }

    if (error) {
      toast({ title: "Failed to save package", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editPkg ? "Package updated!" : "Package created!" });
      onOpenChange(false);
      onSaved();
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-accent" />
            {editPkg ? "Edit Package" : "Create Service Package"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs">Package Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., GST Filing Package" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe what's included..." rows={3} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Service Type *</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Monthly Price (₹) *</Label>
              <Input type="number" value={priceMonthly} onChange={(e) => setPriceMonthly(e.target.value)} placeholder="2000" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Yearly Price (₹)</Label>
              <Input type="number" value={priceYearly} onChange={(e) => setPriceYearly(e.target.value)} placeholder="20000" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Max Subscribers (optional)</Label>
            <Input type="number" value={maxSubs} onChange={(e) => setMaxSubs(e.target.value)} placeholder="Leave blank for unlimited" className="mt-1" />
          </div>

          {/* Includes */}
          <div>
            <Label className="text-xs">What's Included</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                placeholder="e.g., Monthly GST filing"
              />
              <Button type="button" size="sm" onClick={addItem} className="px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {includeItems.length > 0 && (
              <div className="space-y-1.5 mt-2">
                {includeItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-muted/40 rounded-md border border-border text-xs">
                    <CheckCircle2 className="h-3 w-3 text-accent flex-shrink-0" />
                    <span className="flex-1 text-foreground">{item}</span>
                    <button onClick={() => setIncludeItems(includeItems.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleSave} disabled={loading} className="w-full gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {editPkg ? "Update Package" : "Create Package"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Subscribe Dialog ──
function SubscribeDialog({
  pkg, open, onOpenChange, onSubscribed
}: {
  pkg: PackageWithCA; open: boolean; onOpenChange: (v: boolean) => void; onSubscribed: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");

  const handleSubscribe = async () => {
    if (!user || !companyName.trim()) {
      toast({ title: "Company name is required", variant: "destructive" });
      return;
    }
    setLoading(true);

    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + 1);

    const { error } = await supabase.from("package_subscriptions").insert({
      package_id: pkg.id,
      subscriber_user_id: user.id,
      company_name: companyName.trim(),
      status: "active",
      expires_at: expiresAt.toISOString(),
    } as any);

    if (error) {
      toast({ title: "Failed to subscribe", description: error.message, variant: "destructive" });
    } else {
      // Increment subscriber count
      await supabase.from("ca_service_packages").update({
        current_subscribers: pkg.current_subscribers + 1,
        updated_at: new Date().toISOString(),
      } as any).eq("id", pkg.id);

      toast({ title: "Subscribed!", description: `You're now subscribed to ${pkg.name}` });
      onOpenChange(false);
      onSubscribed();
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-accent" /> Subscribe to Package
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Card className="bg-accent/5 border-accent/20">
            <CardContent className="p-3">
              <h3 className="text-sm font-semibold text-foreground">{pkg.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{pkg.ca_profiles?.name} • {pkg.service_type}</p>
              <p className="text-lg font-bold text-foreground mt-2 flex items-center gap-0.5">
                <IndianRupee className="h-4 w-4" />{Number(pkg.price_monthly).toLocaleString("en-IN")}
                <span className="text-xs font-normal text-muted-foreground">/month</span>
              </p>
            </CardContent>
          </Card>
          <div>
            <Label className="text-xs">Your Company Name *</Label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company name" className="mt-1" />
          </div>
          <Button onClick={handleSubscribe} disabled={loading} className="w-full gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Subscribe — ₹{Number(pkg.price_monthly).toLocaleString("en-IN")}/mo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Package Card ──
function PackageCard({
  pkg, isCA, onEdit, onSubscribe
}: {
  pkg: PackageWithCA; isCA: boolean; onEdit?: () => void; onSubscribe?: () => void;
}) {
  const ca = pkg.ca_profiles;
  const isFull = pkg.max_subscribers && pkg.current_subscribers >= pkg.max_subscribers;

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Card className="bg-card border-border hover:border-accent/30 transition-all h-full flex flex-col">
        <CardContent className="p-4 flex flex-col flex-1">
          <div className="flex items-start justify-between mb-2">
            <Badge variant="secondary" className="text-[10px]">{pkg.service_type}</Badge>
            {isFull && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Full</Badge>}
          </div>

          <h3 className="text-sm font-semibold text-foreground">{pkg.name}</h3>
          {pkg.description && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{pkg.description}</p>}

          {ca && !isCA && (
            <div className="flex items-center gap-2 mt-2">
              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                {ca.name.charAt(0)}
              </div>
              <div>
                <span className="text-[11px] font-medium text-foreground flex items-center gap-1">
                  {ca.name} {ca.is_verified && <BadgeCheck className="h-3 w-3 text-accent" />}
                </span>
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-2.5 w-2.5" /> {ca.city} • <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" /> {Number(ca.avg_rating).toFixed(1)}
                </span>
              </div>
            </div>
          )}

          {/* Pricing */}
          <div className="mt-3 pt-3 border-t border-border/30">
            <div className="flex items-baseline gap-1">
              <span className="text-xl font-bold text-foreground flex items-center">
                <IndianRupee className="h-4 w-4" />{Number(pkg.price_monthly).toLocaleString("en-IN")}
              </span>
              <span className="text-xs text-muted-foreground">/month</span>
            </div>
            {pkg.price_yearly && (
              <p className="text-[10px] text-muted-foreground">
                or ₹{Number(pkg.price_yearly).toLocaleString("en-IN")}/year (save {Math.round((1 - Number(pkg.price_yearly) / (Number(pkg.price_monthly) * 12)) * 100)}%)
              </p>
            )}
          </div>

          {/* Includes */}
          {pkg.includes.length > 0 && (
            <div className="mt-3 space-y-1.5 flex-1">
              {pkg.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]">
                  <CheckCircle2 className="h-3 w-3 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          )}

          {/* Subscriber count */}
          <div className="flex items-center gap-2 mt-3 text-[10px] text-muted-foreground">
            <Users className="h-3 w-3" />
            {pkg.current_subscribers} subscriber{pkg.current_subscribers !== 1 ? "s" : ""}
            {pkg.max_subscribers && ` / ${pkg.max_subscribers} max`}
          </div>

          {/* Actions */}
          <div className="mt-3 pt-3 border-t border-border/30">
            {isCA ? (
              <Button variant="outline" size="sm" className="w-full gap-1 text-xs" onClick={onEdit}>
                <Edit2 className="h-3 w-3" /> Edit Package
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full gap-1 text-xs"
                disabled={!!isFull}
                onClick={onSubscribe}
              >
                <ShoppingCart className="h-3 w-3" /> {isFull ? "Fully Booked" : "Subscribe"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ── Main Page ──
const CAServicePackagesPage = () => {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const isCA = role === "ca";
  const [packages, setPackages] = useState<PackageWithCA[]>([]);
  const [mySubscriptions, setMySubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPkg, setEditPkg] = useState<ServicePackage | null>(null);
  const [subscribePkg, setSubscribePkg] = useState<PackageWithCA | null>(null);
  const [tab, setTab] = useState(isCA ? "my-packages" : "browse");

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);

    // Fetch all active packages with CA profiles
    const { data: pkgs } = await supabase
      .from("ca_service_packages")
      .select("*, ca_profiles(name, firm_name, city, avg_rating, is_verified, experience_years, avatar_url)")
      .order("created_at", { ascending: false }) as any;
    if (pkgs) setPackages(pkgs);

    // Fetch user's subscriptions
    if (user && !isCA) {
      const { data: subs } = await supabase
        .from("package_subscriptions")
        .select("*, ca_service_packages(name, price_monthly, service_type, ca_profiles(name))")
        .eq("subscriber_user_id", user.id)
        .order("created_at", { ascending: false }) as any;
      if (subs) setMySubscriptions(subs);
    }

    setLoading(false);
  };

  const myPackages = packages.filter((p) => p.ca_user_id === user?.id);
  const allPackages = packages;

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("ca_service_packages").delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Package deleted" });
      fetchData();
    }
  };

  const cancelSubscription = async (subId: string) => {
    const { error } = await supabase.from("package_subscriptions").update({ status: "cancelled", updated_at: new Date().toISOString() } as any).eq("id", subId);
    if (error) {
      toast({ title: "Failed to cancel", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Subscription cancelled" });
      fetchData();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Package className="h-6 w-6 text-accent" />
            Service Packages
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCA ? "Create and manage your service packages" : "Browse and subscribe to CA service packages"}
          </p>
        </div>
        {isCA && (
          <Button size="sm" className="gap-1.5" onClick={() => { setEditPkg(null); setShowCreate(true); }}>
            <Plus className="h-4 w-4" /> New Package
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {isCA ? (
          <>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Package className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{myPackages.length}</p>
                  <p className="text-[10px] text-muted-foreground">My Packages</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Users className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{myPackages.reduce((s, p) => s + p.current_subscribers, 0)}</p>
                  <p className="text-[10px] text-muted-foreground">Subscribers</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <IndianRupee className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">
                    ₹{myPackages.reduce((s, p) => s + Number(p.price_monthly) * p.current_subscribers, 0).toLocaleString("en-IN")}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Monthly Revenue</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{myPackages.filter((p) => p.is_active).length}</p>
                  <p className="text-[10px] text-muted-foreground">Active Packages</p>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Package className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{allPackages.length}</p>
                  <p className="text-[10px] text-muted-foreground">Available</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <ShoppingCart className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{mySubscriptions.filter((s) => s.status === "active").length}</p>
                  <p className="text-[10px] text-muted-foreground">My Subscriptions</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Content */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted/50">
          {isCA && <TabsTrigger value="my-packages">My Packages ({myPackages.length})</TabsTrigger>}
          <TabsTrigger value="browse">Browse All ({allPackages.length})</TabsTrigger>
          {!isCA && <TabsTrigger value="subscriptions">My Subscriptions ({mySubscriptions.length})</TabsTrigger>}
        </TabsList>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {isCA && (
              <TabsContent value="my-packages" className="mt-4">
                {myPackages.length === 0 ? (
                  <div className="text-center py-16">
                    <Package className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-foreground mb-1">No packages yet</h3>
                    <p className="text-xs text-muted-foreground mb-4">Create your first service package to attract clients</p>
                    <Button size="sm" onClick={() => { setEditPkg(null); setShowCreate(true); }} className="gap-1.5">
                      <Plus className="h-4 w-4" /> Create Package
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {myPackages.map((pkg) => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        isCA={true}
                        onEdit={() => { setEditPkg(pkg); setShowCreate(true); }}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            <TabsContent value="browse" className="mt-4">
              {allPackages.length === 0 ? (
                <div className="text-center py-16">
                  <Package className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">No packages available</h3>
                  <p className="text-xs text-muted-foreground">Check back soon as CAs create service packages</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allPackages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      isCA={isCA}
                      onSubscribe={() => setSubscribePkg(pkg)}
                      onEdit={() => { setEditPkg(pkg); setShowCreate(true); }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {!isCA && (
              <TabsContent value="subscriptions" className="mt-4">
                {mySubscriptions.length === 0 ? (
                  <div className="text-center py-16">
                    <ShoppingCart className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-foreground mb-1">No subscriptions</h3>
                    <p className="text-xs text-muted-foreground">Browse packages and subscribe to a CA's services</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mySubscriptions.map((sub) => {
                      const pkg = sub.ca_service_packages;
                      return (
                        <Card key={sub.id} className="bg-card border-border">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-foreground">{pkg?.name || "Package"}</h3>
                              <p className="text-[11px] text-muted-foreground">
                                {pkg?.ca_profiles?.name} • {pkg?.service_type} • {sub.company_name}
                              </p>
                              <div className="flex items-center gap-3 mt-1">
                                <Badge variant="outline" className={`text-[10px] ${sub.status === "active" ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-muted text-muted-foreground"}`}>
                                  {sub.status}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">
                                  Since {new Date(sub.started_at).toLocaleDateString("en-IN")}
                                </span>
                                {sub.expires_at && (
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                    <Clock className="h-2.5 w-2.5" /> Expires {new Date(sub.expires_at).toLocaleDateString("en-IN")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-bold text-foreground flex items-center">
                                <IndianRupee className="h-3.5 w-3.5" />{pkg ? Number(pkg.price_monthly).toLocaleString("en-IN") : "—"}
                                <span className="text-[10px] font-normal text-muted-foreground">/mo</span>
                              </span>
                              {sub.status === "active" && (
                                <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={() => cancelSubscription(sub.id)}>
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            )}
          </>
        )}
      </Tabs>

      <PackageFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSaved={fetchData}
        editPkg={editPkg}
      />

      {subscribePkg && (
        <SubscribeDialog
          pkg={subscribePkg}
          open={!!subscribePkg}
          onOpenChange={(v) => { if (!v) setSubscribePkg(null); }}
          onSubscribed={fetchData}
        />
      )}
    </div>
  );
};

export default CAServicePackagesPage;
